import React, { useState, useEffect, useCallback } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import api from "../utils/axiosInstance";
import { socket } from "../socket";
import { useModal } from "../context/ModalContext";
import {
  FaFileExcel, FaPlay, FaHistory, FaDownload,
  FaInfoCircle, FaTimes, FaWhatsapp, FaCheckCircle,
  FaTimesCircle, FaClock, FaSpinner, FaPaperPlane,
  FaPause, FaTrashAlt,
} from "react-icons/fa";
import {
  Card, CardHeader, CardBody, PageHeader, Button, Spinner,
  TableWrapper, Table, Th, Td, EmptyState, Input, Select,
  InputGroup,
} from "../components/ui";
import SendWindowPicker, { isSendWindowComplete } from "../components/SendWindowPicker";

export default function SendViaExcel() {
  const modal = useModal();

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionId, setSessionId] = useState("");

  // Form
  const [file, setFile] = useState(null);
  const [campaignName, setCampaignName] = useState("");
  const [defaultMessage, setDefaultMessage] = useState("");
  const [preview, setPreview] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendWindow, setSendWindow] = useState(null);

  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // Guide modal
  const [showGuide, setShowGuide] = useState(false);

  // Fetch sessions
  useEffect(() => {
    api.get("/api/sessions")
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        const list = Array.isArray(data) ? data : [];
        const connected = list.filter((s) => s.status === "connected");
        setSessions(connected);
        if (connected.length > 0) setSessionId(connected[0].sessionId);
      })
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));
  }, []);

  // Fetch campaigns when session changes
  const fetchCampaigns = useCallback(async () => {
    if (!sessionId) return;
    setCampaignsLoading(true);
    try {
      const res = await api.get(`/api/bulk/${sessionId}/all`);
      setCampaigns(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) fetchCampaigns();
  }, [sessionId, fetchCampaigns]);

  // Live campaign progress: backend emits `campaign:progress` after every row.
  // Patch the matching row in state so Delivered/Failed counters tick up
  // without a manual Refresh.
  useEffect(() => {
    const onProgress = (evt) => {
      if (!evt?.campaignId) return;
      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === evt.campaignId || c._id === evt.campaignId
            ? {
                ...c,
                delivered: evt.delivered ?? c.delivered,
                failed: evt.failed ?? c.failed,
                status: evt.status || c.status,
              }
            : c
        )
      );
    };
    socket.on("campaign:progress", onProgress);
    return () => socket.off("campaign:progress", onProgress);
  }, []);

  // Parse file for preview
  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setFile(f);
    if (!f) { setPreview([]); return; }

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(evt.target.result);
        const sheet = wb.worksheets[0];
        const headers = [];
        sheet.getRow(1).eachCell((cell, col) => { headers[col] = cell.value; });
        const rows = [];
        sheet.eachRow((row, rowNum) => {
          if (rowNum === 1) return;
          const obj = {};
          row.eachCell((cell, col) => { if (headers[col]) obj[headers[col]] = cell.value; });
          rows.push(obj);
        });
        setPreview(rows.slice(0, 10));
      } catch {
        setPreview([]);
      }
    };
    reader.readAsArrayBuffer(f);
  };

  // Submit campaign
  const handleSubmit = async () => {
    if (!sessionId) return modal.alert("Select a WhatsApp session first", "warning");
    if (!file) return modal.alert("Upload an Excel or CSV file", "warning");

    if (!isSendWindowComplete(sendWindow)) {
      return modal.alert(
        "Send window is partially filled. Please complete all four fields (start time, end time, days, timezone) or turn it off.",
        "error"
      );
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("campaignName", campaignName || "Untitled Campaign");
    formData.append("bulkMessage", defaultMessage);
    if (isSendWindowComplete(sendWindow) && sendWindow?.sendWindowTimezone) {
      formData.append("sendWindowStart", sendWindow.sendWindowStart);
      formData.append("sendWindowEnd", sendWindow.sendWindowEnd);
      formData.append("sendWindowDays", JSON.stringify(sendWindow.sendWindowDays));
      formData.append("sendWindowTimezone", sendWindow.sendWindowTimezone);
    }

    setSending(true);
    try {
      await api.post(`/api/bulk/${sessionId}/upload`, formData);
      modal.alert("Campaign started! Messages are being sent.", "success");
      setFile(null);
      setCampaignName("");
      setDefaultMessage("");
      setPreview([]);
      // Reset file input
      const fileInput = document.getElementById("excel-file-input");
      if (fileInput) fileInput.value = "";
      fetchCampaigns();
    } catch {
      modal.alert("Failed to start campaign", "error");
    } finally {
      setSending(false);
    }
  };

  // Download sample Excel
  const downloadSample = async () => {
    const sampleData = [
      { number: "919876543210", message: "Hi John! Your order #1234 is confirmed.", mediaUrl: "" },
      { number: "919812345678", message: "Hello! Welcome to our service.", mediaUrl: "" },
      { number: "918123456789", message: "Your appointment is scheduled for tomorrow at 10 AM.", mediaUrl: "https://example.com/image.jpg" },
    ];
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Messages");
    ws.columns = [
      { header: "number", key: "number", width: 20 },
      { header: "message", key: "message", width: 50 },
      { header: "mediaUrl", key: "mediaUrl", width: 40 },
    ];
    sampleData.forEach(row => ws.addRow(row));
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "walytic-bulk-sample.xlsx");
  };

  // Status badge
  const statusBadge = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "completed") return <span className="badge-success flex items-center gap-1"><FaCheckCircle size={9} /> Completed</span>;
    if (s === "processing") return <span className="badge-info flex items-center gap-1"><FaSpinner size={9} className="animate-spin" /> Processing</span>;
    if (s === "scheduled") return <span className="badge-warning flex items-center gap-1"><FaClock size={9} /> Scheduled</span>;
    if (s === "paused") return <span className="badge-warning flex items-center gap-1"><FaPause size={9} /> Paused</span>;
    if (s === "failed") return <span className="badge-danger flex items-center gap-1"><FaTimesCircle size={9} /> Failed</span>;
    return <span className="badge-neutral">{status}</span>;
  };

  // Export campaign results as Excel download
  const handleExport = async (campaignId, campaignNameStr) => {
    try {
      const res = await api.get(`/api/bulk/${sessionId}/campaigns/${campaignId}/export`, {
        responseType: "blob",
      });
      const safeName = (campaignNameStr || "campaign").replace(/[^a-zA-Z0-9_-]/g, "_");
      saveAs(new Blob([res.data]), `${safeName}-results.xlsx`);
    } catch {
      modal.alert("Failed to export campaign results", "error");
    }
  };

  // Pause a running campaign
  const handlePause = async (campaignId) => {
    try {
      await api.put(`/api/bulk/${sessionId}/campaigns/${campaignId}/pause`);
      modal.alert("Pause signal sent. Campaign will stop after current message.", "success");
      setTimeout(fetchCampaigns, 2000);
    } catch (err) {
      modal.alert(err.response?.data?.error || "Failed to pause campaign", "error");
    }
  };

  // Resume a paused campaign
  const handleResume = async (campaignId) => {
    try {
      const res = await api.put(`/api/bulk/${sessionId}/campaigns/${campaignId}/resume`);
      modal.alert(res.data?.message || "Campaign resumed.", "success");
      setTimeout(fetchCampaigns, 2000);
    } catch (err) {
      modal.alert(err.response?.data?.error || "Failed to resume campaign", "error");
    }
  };

  // Delete a campaign
  const handleDelete = async (campaignId, campaignNameStr) => {
    const confirmed = await modal.confirm(
      `Delete campaign "${campaignNameStr || "Untitled"}"? This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await api.delete(`/api/bulk/${sessionId}/campaigns/${campaignId}`);
      modal.alert("Campaign deleted.", "success");
      fetchCampaigns();
    } catch (err) {
      modal.alert(err.response?.data?.error || "Failed to delete campaign", "error");
    }
  };

  const hasCustomMessages = preview.length > 0 && preview.some((r) => r.message || r.Message);

  return (
    <div className="page-content">
      <PageHeader
        title="Send via Excel"
        subtitle="Upload an Excel file with numbers and custom messages to send in bulk"
        guideUrl="/guide/send-via-excel"
      />

      {/* Session Selector */}
      <Card className="mb-5">
        <CardBody>
          <InputGroup label="WhatsApp Session">
            {sessionsLoading ? (
              <div className="flex items-center gap-2 text-ink-muted text-sm py-2">
                <Spinner size="sm" /> Loading sessions...
              </div>
            ) : sessions.length > 0 ? (
              <Select value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                {sessions.map((s) => (
                  <option key={s.sessionId} value={s.sessionId}>
                    {s.phone || s.sessionId} — {s.name || "WhatsApp Session"}
                  </option>
                ))}
              </Select>
            ) : (
              <div className="flex items-center gap-2 text-sm text-ink-muted py-2">
                <FaWhatsapp className="text-ink-faint" />
                No connected sessions. Connect one from WhatsApp Phones.
              </div>
            )}
          </InputGroup>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {/* Left: Upload Form */}
        <div className="lg:col-span-2 space-y-5">
          <Card>
            <CardHeader className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FaPaperPlane size={13} className="text-brand" />
                <span className="font-semibold text-ink-primary text-sm">New Campaign</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowGuide(true)}
                  className="flex items-center gap-1.5 text-xs text-brand hover:text-brand-700 font-medium transition-colors"
                >
                  <FaInfoCircle size={12} /> Format Guide
                </button>
                <Button variant="secondary" size="sm" onClick={downloadSample}>
                  <FaDownload size={10} className="mr-1.5" /> Sample File
                </Button>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              {/* Campaign Name */}
              <InputGroup label="Campaign Name">
                <Input
                  placeholder="e.g. March Promotion, Order Confirmations"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                />
              </InputGroup>

              {/* File Upload */}
              <InputGroup label="Upload Excel / CSV File">
                <label className="flex items-center justify-center w-full px-4 py-4 rounded-lg border-2 border-dashed border-surface-border hover:border-brand/40 bg-surface-bg cursor-pointer transition-colors">
                  <div className="flex flex-col items-center gap-1.5 text-center">
                    <FaFileExcel size={22} className="text-ink-faint" />
                    <span className="text-xs text-ink-muted">
                      {file ? file.name : "Click to select .xlsx, .xls, or .csv file"}
                    </span>
                    <span className="text-2xs text-ink-faint">
                      Columns: number (required), message (optional), mediaUrl (optional)
                    </span>
                  </div>
                  <input
                    id="excel-file-input"
                    type="file"
                    className="hidden"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                  />
                </label>
              </InputGroup>

              {/* Default Message (fallback) */}
              <InputGroup label={<span>Default Message <span className="text-ink-faint font-normal">(used when message column is empty)</span></span>}>
                <textarea
                  className="input min-h-[80px] resize-y"
                  placeholder="Enter a default message for rows without a custom message..."
                  value={defaultMessage}
                  onChange={(e) => setDefaultMessage(e.target.value)}
                />
              </InputGroup>

              {/* Preview */}
              {preview.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-ink-primary">
                      Preview ({preview.length} of {preview.length >= 10 ? "10+" : preview.length} rows)
                    </p>
                    {hasCustomMessages && (
                      <span className="badge-success text-2xs">Custom messages detected</span>
                    )}
                  </div>
                  <div className="rounded-lg border border-surface-border overflow-hidden max-h-[240px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-surface-bg sticky top-0">
                          <th className="text-left px-3 py-2 text-ink-muted font-semibold">#</th>
                          <th className="text-left px-3 py-2 text-ink-muted font-semibold">Number</th>
                          <th className="text-left px-3 py-2 text-ink-muted font-semibold">Message</th>
                          <th className="text-left px-3 py-2 text-ink-muted font-semibold">Media</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((row, i) => (
                          <tr key={i} className="border-t border-surface-border">
                            <td className="px-3 py-2 text-ink-faint">{i + 1}</td>
                            <td className="px-3 py-2 font-mono text-ink-primary">
                              {row.number || row.Number || row.To || row.phone || "—"}
                            </td>
                            <td className="px-3 py-2 text-ink-secondary max-w-[200px] truncate">
                              {row.message || row.Message || <span className="text-ink-faint italic">default</span>}
                            </td>
                            <td className="px-3 py-2 text-ink-muted truncate max-w-[120px]">
                              {row.mediaUrl || row["Image/File Url (Optional)"] || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Send window (optional) */}
              <SendWindowPicker value={sendWindow} onChange={setSendWindow} />

              {/* Submit */}
              <Button
                variant="primary"
                className="w-full"
                onClick={handleSubmit}
                loading={sending}
                disabled={!sessionId || !file}
              >
                <FaPlay size={10} className="mr-2" />
                Start Campaign
              </Button>
            </CardBody>
          </Card>
        </div>

        {/* Right: Info panels */}
        <div className="space-y-5">
          {/* How it works */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-semibold text-ink-primary mb-3">How It Works</h3>
              <div className="space-y-3">
                {[
                  { step: "1", title: "Prepare your Excel file", desc: "Column A: phone numbers, Column B: custom messages" },
                  { step: "2", title: "Upload & preview", desc: "We'll show you a preview of what will be sent" },
                  { step: "3", title: "Start campaign", desc: "Messages are sent with smart delays to avoid bans" },
                ].map((s) => (
                  <div key={s.step} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-brand/10 text-brand text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {s.step}
                    </span>
                    <div>
                      <p className="text-xs font-semibold text-ink-primary">{s.title}</p>
                      <p className="text-2xs text-ink-muted">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          {/* Column Reference */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-semibold text-ink-primary mb-3">Column Reference</h3>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <code className="text-2xs bg-brand/10 text-brand px-1.5 py-0.5 rounded font-mono flex-shrink-0">number</code>
                  <span className="text-xs text-ink-muted">Phone number with country code (required)</span>
                </div>
                <div className="flex items-start gap-2">
                  <code className="text-2xs bg-brand/10 text-brand px-1.5 py-0.5 rounded font-mono flex-shrink-0">message</code>
                  <span className="text-xs text-ink-muted">Custom message per recipient (optional)</span>
                </div>
                <div className="flex items-start gap-2">
                  <code className="text-2xs bg-brand/10 text-brand px-1.5 py-0.5 rounded font-mono flex-shrink-0">mediaUrl</code>
                  <span className="text-xs text-ink-muted">Image/video/document URL (optional)</span>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Tips */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-semibold text-ink-primary mb-2 flex items-center gap-2">
                <FaInfoCircle size={11} className="text-brand" /> Tips
              </h3>
              <ul className="space-y-1.5 text-xs text-ink-muted">
                <li className="flex items-start gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-brand mt-1.5 flex-shrink-0" />
                  Include country code (e.g. 919876543210)
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-brand mt-1.5 flex-shrink-0" />
                  Use *bold* and _italic_ in messages
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-brand mt-1.5 flex-shrink-0" />
                  Keep under 150 messages per session per day
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-yellow-500 mt-1.5 flex-shrink-0" />
                  Rows without a message column use the default message
                </li>
              </ul>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Campaign History */}
      <Card>
        <CardHeader className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaHistory size={13} className="text-brand" />
            <span className="font-semibold text-ink-primary text-sm">
              Campaign History {campaigns.length > 0 && `(${campaigns.length})`}
            </span>
          </div>
          {campaigns.length > 0 && (
            <Button variant="secondary" size="sm" onClick={fetchCampaigns}>
              Refresh
            </Button>
          )}
        </CardHeader>
        {campaignsLoading ? (
          <CardBody>
            <div className="flex items-center justify-center py-8"><Spinner size="md" /></div>
          </CardBody>
        ) : campaigns.length > 0 ? (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Campaign</Th>
                  <Th>Total</Th>
                  <Th>Delivered</Th>
                  <Th>Failed</Th>
                  <Th>Status</Th>
                  <Th>Date</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id}>
                    <Td className="font-medium">{c.name || "Untitled"}</Td>
                    <Td>{c.total || 0}</Td>
                    <Td>
                      <span className="text-green-600 font-medium">{c.delivered || 0}</span>
                      {c.total > 0 && (
                        <span className="text-ink-faint text-xs ml-1">
                          ({Math.round(((c.delivered || 0) / c.total) * 100)}%)
                        </span>
                      )}
                    </Td>
                    <Td>
                      {(c.failed || 0) > 0 ? (
                        <span className="text-red-500 font-medium">{c.failed}</span>
                      ) : (
                        <span className="text-ink-faint">0</span>
                      )}
                    </Td>
                    <Td>{statusBadge(c.status)}</Td>
                    <Td className="text-xs text-ink-muted">
                      {c.createdAt ? new Date(c.createdAt).toLocaleString() : "—"}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1.5">
                        {/* Export */}
                        {["completed", "failed", "paused"].includes(c.status) && (
                          <button
                            onClick={() => handleExport(c.id, c.name)}
                            className="btn-icon text-brand hover:bg-brand/10"
                            title="Export results"
                          >
                            <FaDownload size={12} />
                          </button>
                        )}
                        {/* Pause */}
                        {c.status === "processing" && (
                          <button
                            onClick={() => handlePause(c.id)}
                            className="btn-icon text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-950/30"
                            title="Pause campaign"
                          >
                            <FaPause size={12} />
                          </button>
                        )}
                        {/* Resume */}
                        {c.status === "paused" && (
                          <button
                            onClick={() => handleResume(c.id)}
                            className="btn-icon text-brand hover:bg-brand/10"
                            title="Resume campaign"
                          >
                            <FaPlay size={12} />
                          </button>
                        )}
                        {/* Delete */}
                        {c.status !== "processing" && (
                          <button
                            onClick={() => handleDelete(c.id, c.name)}
                            className="btn-icon text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"
                            title="Delete campaign"
                          >
                            <FaTrashAlt size={11} />
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        ) : (
          <CardBody>
            <EmptyState
              icon={FaPaperPlane}
              title="No campaigns yet"
              description="Upload an Excel file and start your first campaign."
            />
          </CardBody>
        )}
      </Card>

      {/* Format Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={() => setShowGuide(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative bg-surface-card rounded-xl shadow-2xl border border-surface-border w-full max-w-lg animate-modal-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-surface-border">
              <h3 className="text-base font-semibold text-ink-primary">Excel Format Guide</h3>
              <button onClick={() => setShowGuide(false)} className="btn-icon"><FaTimes size={14} /></button>
            </div>

            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Excel structure */}
              <div>
                <h4 className="text-xs font-semibold text-brand mb-2 uppercase tracking-wide">Required Structure</h4>
                <div className="rounded-lg border border-surface-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-surface-bg">
                        <th className="text-left px-3 py-2 text-xs font-semibold text-ink-muted w-8"></th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-ink-muted">A (number)</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-ink-muted">B (message)</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-ink-muted">C (mediaUrl)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-surface-border bg-brand/5">
                        <td className="px-3 py-2 text-xs text-ink-faint font-semibold">1</td>
                        <td className="px-3 py-2 text-xs font-mono text-ink-primary">number</td>
                        <td className="px-3 py-2 text-xs font-mono text-ink-primary">message</td>
                        <td className="px-3 py-2 text-xs font-mono text-ink-primary">mediaUrl</td>
                      </tr>
                      {[
                        ["2", "919876543210", "Hi John! Order confirmed.", ""],
                        ["3", "918123456789", "Hello! Your appointment is tomorrow.", ""],
                        ["4", "917654321098", "Check this out!", "https://example.com/img.jpg"],
                      ].map(([row, num, msg, media]) => (
                        <tr key={row} className="border-t border-surface-border">
                          <td className="px-3 py-2 text-xs text-ink-faint font-semibold">{row}</td>
                          <td className="px-3 py-2 text-xs font-mono text-ink-secondary">{num}</td>
                          <td className="px-3 py-2 text-xs text-ink-secondary">{msg}</td>
                          <td className="px-3 py-2 text-xs text-ink-muted truncate max-w-[100px]">{media || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Column details */}
              <div>
                <h4 className="text-xs font-semibold text-brand mb-2 uppercase tracking-wide">Column Details</h4>
                <div className="space-y-2">
                  <div className="p-2.5 rounded-lg border border-surface-border">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-2xs bg-brand/10 text-brand px-1.5 py-0.5 rounded font-mono">number</code>
                      <span className="badge-danger text-2xs">Required</span>
                    </div>
                    <p className="text-xs text-ink-muted">Phone number with country code. No +, spaces, or dashes needed.</p>
                  </div>
                  <div className="p-2.5 rounded-lg border border-surface-border">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-2xs bg-brand/10 text-brand px-1.5 py-0.5 rounded font-mono">message</code>
                      <span className="badge-neutral text-2xs">Optional</span>
                    </div>
                    <p className="text-xs text-ink-muted">Custom message per row. If empty, the default message is used. Supports *bold* and _italic_.</p>
                  </div>
                  <div className="p-2.5 rounded-lg border border-surface-border">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-2xs bg-brand/10 text-brand px-1.5 py-0.5 rounded font-mono">mediaUrl</code>
                      <span className="badge-neutral text-2xs">Optional</span>
                    </div>
                    <p className="text-xs text-ink-muted">Direct URL to an image, video, or document. Sent as an attachment with the message as caption.</p>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="alert-warning flex items-start gap-2">
                <FaInfoCircle size={12} className="mt-0.5 flex-shrink-0" />
                <span className="text-xs">Row 1 must be the header row with column names: <code className="font-mono">number</code>, <code className="font-mono">message</code>, <code className="font-mono">mediaUrl</code></span>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-surface-border flex items-center justify-between">
              <Button variant="secondary" size="sm" onClick={downloadSample}>
                <FaDownload size={10} className="mr-1.5" /> Download Sample
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setShowGuide(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
